import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Card, Stack, Typography, Button, Chip, Divider, CircularProgress, Dialog,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { dataFetcher, baseUrl, publicAxios, getToken, endpoints } from 'Api';
import { AppContext } from 'Context';
import { ConfirmDialog } from 'Components/ConfirmDialog';
import MembershipCheckout from 'Pages/userManagement/MembershipDemo/MembershipCheckout';
import MembershipUpgradeDialogV2 from './MembershipUpgradeDialogV2';

const money = (cents) => `$${Math.round((Number(cents) || 0) / 100).toLocaleString('en-US')}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};
const pricingOf = (plan) => plan?.pricing?.find((p) => p.billing_title === 'Annual') || plan?.pricing?.[0];

// A creation discount → short label. 100% off reads as Complimentary.
const fmtDisc = (d) => {
  if (!d || !d.value) return '';
  if (d.type === 'percentage') return Number(d.value) === 100 ? 'Complimentary' : `${d.value}% off`;
  return `$${(Number(d.value) / 100).toFixed(2)} off`;
};

const DARK_BTN = { bgcolor: '#15171c', textTransform: 'none', fontWeight: 700, borderRadius: 2, '&:hover': { bgcolor: '#000' } };

// Membership state panel for the V2 Manage Users page. Reuses the ported
// useMembership result (passed in as props) and the Stripe checkout flow.
const MembershipPanelV2 = ({
  loading, isMember, status, membership, refetch,
}) => {
  const { setAppSnackbar } = useContext(AppContext);
  const [busy, setBusy] = useState(null); // 'manage' | 'cancel' | 'resume'
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [plans, setPlans] = useState(null);
  const [cycleByPlan, setCycleByPlan] = useState({}); // per-plan billing cycle
  // Creation-allowance chips ("New users you can create this cycle") need the
  // plan_id → product name map, resolved best-effort from getAllActivePlans.
  const [planNames, setPlanNames] = useState({});
  const [selected, setSelected] = useState(null); // { plan, pricing } → checkout dialog
  // Returning from Stripe hosted checkout / Payment Element with a session/sub id.
  const [confirming, setConfirming] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return !!(p.get('session_id') || p.get('subscription_id'));
  });

  const m = membership || {};
  const canceling = !!m.cancel_at_period_end;
  const periodEnd = fmtDate(m.current_period_end);
  // Per-cycle "create a user on this plan" allowances + discounts granted by the
  // membership. Flatten each creation into one-or-more offer tiles:
  //  • numeric `allowed` + `discount` → a capped free tier (bar, "X left").
  //  • `after_limit_discount` on a capped plan → a SEPARATE uncapped tile that
  //    kicks in once the free tier is used up (count = after_limit_used_this_cycle).
  //  • `allowed: null` + a discount → a single flat, uncapped discount tile.
  //  • no discount + no after-limit discount → not an offer, so it's dropped.
  const creations = Array.isArray(m.creations) ? m.creations : [];
  const offerTiles = [];
  creations.forEach((c) => {
    const name = planNames[String(c.plan_id)] || `Plan ${c.plan_id}`;
    const capped = c.allowed !== null && c.allowed !== undefined;
    if (capped) {
      const allowed = Number(c.allowed) || 0;
      const used = Number(c.used_this_cycle) || 0;
      const exceeded = !!c.limit_exceeded || (allowed > 0 && used >= allowed);
      const remaining = c.free_remaining != null ? Number(c.free_remaining) : Math.max(0, allowed - used);
      if (c.discount) {
        offerTiles.push({
          key: `${c.plan_id}-free`,
          name,
          badge: fmtDisc(c.discount),
          count: `${used}/${allowed}`,
          pct: allowed > 0 ? Math.min(100, Math.round((used / allowed) * 100)) : 0,
          bar: true,
          exceeded,
          detail: exceeded ? 'Free tier used up' : `${remaining} free left`,
        });
      }
      if (c.after_limit_discount) {
        offerTiles.push({
          key: `${c.plan_id}-after`,
          name,
          note: 'After free tier',
          badge: fmtDisc(c.after_limit_discount),
          count: '∞',
          bar: false,
          exceeded: false,
          detail: `${Number(c.after_limit_used_this_cycle) || 0} used`,
        });
      }
    } else {
      const disc = c.discount || c.after_limit_discount;
      if (disc) {
        offerTiles.push({
          key: `${c.plan_id}-flat`,
          name,
          badge: fmtDisc(disc),
          count: '∞',
          bar: false,
          exceeded: false,
          detail: `${Number(c.used_this_cycle) || 0} created`,
        });
      }
    }
  });
  const noticeStatuses = ['canceled', 'expired', 'past_due', 'unpaid', 'paused'];
  const paymentIssue = ['past_due', 'unpaid', 'paused'].includes(status);
  const showNotice = !isMember && membership && noticeStatuses.includes(status);
  const showPlans = !loading && !isMember && !showNotice;

  useEffect(() => {
    if (!showPlans || plans !== null) return;
    (async () => {
      try {
        const token = getToken();
        const { data } = await publicAxios.get(`${baseUrl}/v4/membership-plans`, token ? { params: { token } } : undefined);
        setPlans(Array.isArray(data?.data) ? data.data : []);
      } catch (e) { setPlans([]); }
    })();
  }, [showPlans, plans]);

  // Resolve creation plan_id → product name (best-effort; falls back to id).
  useEffect(() => {
    if (!isMember || !creations.length) return undefined;
    let active = true;
    (async () => {
      try {
        const res = await dataFetcher(endpoints.getAllActivePlans);
        if (active && res?.success && Array.isArray(res.data)) {
          const map = {};
          res.data.forEach((p) => { map[String(p.id)] = p.product_name; });
          setPlanNames(map);
        }
      } catch (e) { /* names are best-effort */ }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMember, creations.length]);

  const toast = (type, message) => setAppSnackbar?.({ isVisible: true, type, message });

  // Confirm a checkout return via order-status (source of truth; no webhook
  // needed — the backend reads live from Stripe). Poll a few times if pending.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sessionId = p.get('session_id');
    const subId = p.get('subscription_id');
    const cancelled = p.get('checkout') === 'cancelled';
    const cleanUrl = () => window.history.replaceState({}, '', window.location.pathname);
    if (cancelled) { cleanUrl(); return undefined; }
    if (!sessionId && !subId) return undefined;
    let alive = true;
    let tries = 0;
    const query = sessionId ? { session_id: sessionId } : { subscription_id: subId };
    const poll = async () => {
      try {
        const { data } = await publicAxios.get(`${baseUrl}/v4/membership/order-status`, {
          params: { ...query, token: getToken() },
        });
        if (!alive) return;
        const st = data?.data?.status;
        if (st === 'active') {
          toast('success', 'Payment successful — your membership is active.');
          cleanUrl(); setConfirming(false); refetch?.(true); return;
        }
        if (st === 'failed') {
          toast('error', 'Payment failed. You were not charged.');
          cleanUrl(); setConfirming(false); return;
        }
        if (tries < 5) { tries += 1; setTimeout(poll, 2000); return; }
        toast('success', "Payment received — finalizing. We'll confirm by email.");
        cleanUrl(); setConfirming(false); refetch?.(true);
      } catch (e) {
        if (!alive) return;
        toast('error', 'Could not confirm your payment.');
        cleanUrl(); setConfirming(false);
      }
    };
    poll();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back button from Stripe (portal/checkout) restores the frozen page from
  // bfcache with a button still "loading" — clear it when the page is shown.
  useEffect(() => {
    const onShow = (e) => { if (e.persisted) setBusy(null); };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  const openPortal = async () => {
    setBusy('manage');
    try {
      const res = await dataFetcher('v4/membership/billing-portal', { return_url: window.location.href });
      if (res?.success && res?.data?.url) window.location = res.data.url;
      else { toast('error', res?.message || 'Could not open the billing portal.'); setBusy(null); }
    } catch (e) { toast('error', e?.response?.data?.message || 'Could not open the billing portal.'); setBusy(null); }
  };

  const doCancel = async () => {
    setBusy('cancel');
    try {
      const res = await dataFetcher('v4/membership/cancel');
      if (res?.success) {
        toast('success', res.message || 'Your membership will end at the current period.');
        setConfirmCancel(false);
        refetch?.(true);
      } else toast('error', res?.message || 'Could not cancel your membership.');
    } catch (e) { toast('error', e?.response?.data?.message || 'Could not cancel your membership.'); } finally { setBusy(null); }
  };

  const doResume = async () => {
    setBusy('resume');
    try {
      const res = await dataFetcher('v4/membership/resume');
      if (res?.success) {
        toast('success', res.message || 'Membership resumed.');
        refetch?.(true);
      } else toast('error', res?.message || 'Could not resume your membership.');
    } catch (e) {
      toast('error', e?.response?.data?.message || 'Could not resume your membership.');
      if (e?.response?.status === 409) refetch?.(true);
    } finally { setBusy(null); }
  };

  if (confirming) {
    return (
      <Card sx={{
        p: 3, borderRadius: 2.5, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
      }}
      >
        <CircularProgress size={22} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#15171c' }}>Finalizing your membership…</Typography>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card sx={{ p: 3, borderRadius: 2.5, mb: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={26} />
      </Card>
    );
  }

  // ── Active member ───────────────────────────────────────────────
  if (isMember) {
    return (
      <>
        <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2.5, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#9aa1ab' }}>
                Your membership
              </Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap', mt: 0.5 }} useFlexGap>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#15171c' }}>{m.plan_name}</Typography>
                {m.billing_title && <Chip size="small" label={m.billing_title} sx={{ height: 22, fontWeight: 700 }} />}
                <Chip
                  size="small"
                  label={canceling ? 'Ending' : (m.status || 'Active')}
                  sx={{ height: 22, fontWeight: 700, textTransform: 'capitalize', color: canceling ? '#b26a00' : '#1f7a45', bgcolor: canceling ? 'rgba(214,158,46,0.16)' : 'rgba(31,157,87,0.14)' }}
                />
              </Stack>
              <Typography sx={{ mt: 0.5, fontSize: 13.5, color: '#6b7280' }}>
                {money(m.amount_cents)}
                {periodEnd ? ` · ${canceling ? 'Ends' : 'Renews'} on ${periodEnd}` : ''}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }} useFlexGap flexWrap="wrap">
              {m.can_upgrade && !canceling && (
                <Button variant="outlined" size="small" disableElevation onClick={() => setUpgradeOpen(true)} disabled={!!busy} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, color: '#15171c', borderColor: '#15171c', '&:hover': { borderColor: '#000', bgcolor: 'rgba(21,23,28,0.04)' } }}>
                  Upgrade
                </Button>
              )}
              <Button variant="contained" size="small" disableElevation onClick={openPortal} disabled={!!busy} sx={DARK_BTN}>
                {busy === 'manage' ? 'Opening…' : 'Manage membership'}
              </Button>
              {canceling ? (
                <Button variant="contained" color="success" size="small" disableElevation onClick={doResume} disabled={!!busy} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                  {busy === 'resume' ? 'Resuming…' : 'Resume'}
                </Button>
              ) : (
                <Button variant="text" color="error" size="small" onClick={() => setConfirmCancel(true)} disabled={!!busy} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Cancel membership
                </Button>
              )}
            </Stack>
          </Stack>
          {canceling && periodEnd && (
            <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2, bgcolor: 'rgba(214,158,46,0.1)', color: '#b26a00', fontSize: 13, fontWeight: 600 }}>
              {`Your membership ends on ${periodEnd}. You keep full access until then.`}
            </Box>
          )}
          {offerTiles.length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eef1f6' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#9aa1ab', mb: 1 }}>
                New users you can create this cycle
              </Typography>
              <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
                {offerTiles.map((t) => {
                  const isFree = t.badge === 'Complimentary';
                  const accent = t.exceeded ? '#b26a00' : '#1f7a45';
                  const badgeSx = isFree
                    ? { bgcolor: 'rgba(31,157,87,0.14)', color: '#1f7a45' }
                    : { bgcolor: 'rgba(21,23,28,0.06)', color: '#15171c' };
                  return (
                    <Box
                      key={t.key}
                      title={`${t.name} — ${t.badge}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        minWidth: 190,
                        px: 1.5,
                        py: 0.9,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: t.exceeded ? 'rgba(178,106,0,0.35)' : '#e5e7eb',
                        bgcolor: t.exceeded ? 'rgba(214,158,46,0.06)' : '#fff',
                      }}
                    >
                      <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#15171c' }} noWrap>{t.name}</Typography>
                          {t.note && (
                            <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: '#9aa1ab', textTransform: 'uppercase', letterSpacing: 0.3 }} noWrap>
                              {t.note}
                            </Typography>
                          )}
                        </Box>
                        {t.badge && (
                          <Box component="span" sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3, px: 0.9, py: '2px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0, ...badgeSx }}>
                            {t.badge}
                          </Box>
                        )}
                      </Stack>
                      {/* Footer pinned to the bottom so the count/∞ line sits at
                          the same height across tiles, regardless of note/bar. The
                          bar row is always reserved (transparent when absent). */}
                      <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <Typography sx={{ fontSize: 12, color: '#6b7280' }} noWrap>{t.detail}</Typography>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: accent, flexShrink: 0 }}>{t.count}</Typography>
                        </Stack>
                        <Box sx={{ height: 4, borderRadius: 999, bgcolor: t.bar ? '#eef1f6' : 'transparent', overflow: 'hidden' }}>
                          {t.bar && (
                            <Box sx={{ height: '100%', width: `${t.pct}%`, borderRadius: 999, bgcolor: t.exceeded ? '#b26a00' : '#1f9d57' }} />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Card>
        <ConfirmDialog
          open={confirmCancel}
          title="Cancel membership?"
          description={`Cancel your ${m.plan_name || ''} membership? You'll keep access until ${periodEnd || 'the end of the current period'}.`}
          confirmLabel="Yes, cancel"
          cancelLabel="Keep membership"
          loading={busy === 'cancel'}
          onConfirm={doCancel}
          onCancel={() => setConfirmCancel(false)}
        />
        <MembershipUpgradeDialogV2
          open={upgradeOpen}
          membership={m}
          onClose={() => setUpgradeOpen(false)}
          onUpgraded={() => {
            setUpgradeOpen(false);
            toast('success', 'Membership upgraded.');
            refetch?.(true);
          }}
        />
      </>
    );
  }

  // ── Ended / payment issue ───────────────────────────────────────
  if (showNotice) {
    const planLine = `${m.plan_name || 'Your'} ${m.billing_title || ''}`.trim();
    const msg = paymentIssue
      ? `We couldn't process payment for your ${planLine} membership. Update your card to keep it active.`
      : `Your ${planLine} membership ${status === 'expired' ? 'expired' : 'ended'}${periodEnd ? ` on ${periodEnd}` : ''}.`;
    // eslint-disable-next-line no-nested-ternary
    const noticeTitle = paymentIssue
      ? 'Payment problem'
      : (status === 'expired' ? 'Membership expired' : 'Membership ended');
    return (
      <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#15171c' }}>
              {noticeTitle}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13.5, color: '#6b7280', maxWidth: 520 }}>{msg}</Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }} useFlexGap flexWrap="wrap">
            {paymentIssue && (
              <Button variant="contained" size="small" disableElevation onClick={openPortal} disabled={!!busy} sx={DARK_BTN}>
                {busy === 'manage' ? 'Opening…' : (status === 'paused' ? 'Manage' : 'Update your card')}
              </Button>
            )}
            <Button
              variant={paymentIssue ? 'outlined' : 'contained'}
              size="small"
              disableElevation
              onClick={() => { setPlans(null); refetch?.(true); }}
              sx={paymentIssue ? { textTransform: 'none', fontWeight: 700, borderRadius: 2 } : DARK_BTN}
            >
              Re-subscribe
            </Button>
          </Stack>
        </Stack>
      </Card>
    );
  }

  // ── Not a member → plan selection ───────────────────────────────
  return (
    <Card sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2.5, mb: 2 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#15171c' }}>Become a member</Typography>
      <Typography sx={{ mt: 0.5, mb: 2, fontSize: 13.5, color: '#6b7280' }}>
        Subscribe to unlock member pricing and create & manage users.
      </Typography>

      {plans === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : !plans.length ? (
        <Typography sx={{ color: '#6b7280', fontSize: 14, py: 2 }}>No plans available right now.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
          {plans.map((plan) => {
            const cycles = (plan.pricing || []).map((p) => p.billing_title);
            const cycle = cycleByPlan[plan.id] || (cycles.includes('Annual') ? 'Annual' : cycles[0]);
            const pricing = plan.pricing?.find((p) => p.billing_title === cycle) || pricingOf(plan);
            if (!pricing) return null;
            const showcase = pricing.benefits?.showcase || [];
            return (
              <Box key={plan.id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2.5, p: 2, display: 'flex', flexDirection: 'column', flex: '1 1 220px', minWidth: 0, maxWidth: { xs: '100%', sm: 300 } }}>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#15171c' }}>{plan.name}</Typography>
                <Typography sx={{ mt: 1, mb: 1.5 }}>
                  <Box component="span" sx={{ fontSize: 24, fontWeight: 800, color: '#15171c' }}>{money(pricing.price_cents)}</Box>
                  <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: '#9aa1ab' }}>{` / ${pricing.billing_title}`}</Box>
                </Typography>
                <Stack spacing={0.75} sx={{ flex: 1, mb: 2 }}>
                  {showcase.map((b, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Stack key={i} direction="row" spacing={0.75} sx={{ alignItems: 'flex-start' }}>
                      <CheckRoundedIcon sx={{ fontSize: 16, color: '#1f9d57', mt: '2px' }} />
                      <Typography sx={{ fontSize: 13, color: '#374151' }}>{b.title}</Typography>
                    </Stack>
                  ))}
                </Stack>
                {cycles.length > 1 && (
                  <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, alignSelf: 'center', bgcolor: 'action.hover', p: 0.5, borderRadius: 999 }}>
                    {plan.pricing.map((p) => {
                      const active = p.billing_title === cycle;
                      return (
                        <Button
                          key={p.billing_title}
                          size="small"
                          disableElevation
                          variant={active ? 'contained' : 'text'}
                          onClick={() => setCycleByPlan((prev) => ({ ...prev, [plan.id]: p.billing_title }))}
                          sx={{ borderRadius: 999, minWidth: 0, px: 1.5, py: 0.25, textTransform: 'none', fontWeight: 700, fontSize: 12, color: active ? undefined : 'text.secondary' }}
                        >
                          {p.billing_title}
                        </Button>
                      );
                    })}
                  </Stack>
                )}
                <Button variant="contained" disableElevation fullWidth onClick={() => setSelected({ plan, pricing })} sx={DARK_BTN}>
                  {`Choose ${plan.name}`}
                </Button>
              </Box>
            );
          })}
        </Box>
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {selected && (
            <MembershipCheckout
              plan={selected.plan}
              pricing={selected.pricing}
              onBack={() => setSelected(null)}
              onSuccess={() => { setSelected(null); refetch?.(); }}
            />
          )}
        </Box>
      </Dialog>
      <Divider sx={{ display: 'none' }} />
    </Card>
  );
};

export default MembershipPanelV2;
